import { Tag } from "components/DesignSystem/Tag/Tag";
import {
  FILTER_END_DATE,
  FILTER_SELECT_PERIOD,
  FILTER_START_DATE,
} from "components/TaskTracker/Task1120FilterOptions";
import { DateSelectHeader } from "components/Ticket/ColumnHeader/DateSelectHeader";
import { MultiselectHeader } from "components/Ticket/ColumnHeader/MultiselectHeader";
import {
  BROADCAST_MEDIUM_OPTIONS,
  BROADCAST_STATUS_COLORS,
  BROADCAST_STATUSES_DISPLAY,
  IVR_STATUS_COLORS,
  IVR_STATUSES_DISPLAY,
} from "constants/broadcast";
import dayjs from "dayjs";
import { createColumnHelper } from "react-table-8.10.7";
import { BroadcastFilterOptions } from "types/Models/broadcast";
import { Broadcast } from "types/Models/broadcastCall";
import { formatDate } from "utils/formatDate";
import { BroadcastCallOptions } from "./BroadcastCallOptions";
import { BroadcastMedium } from "./BroadcastMedium";
import { convertDisplayToOptions } from "components/DesignSystem/Combobox/utils/convertDisplayToOptions";

const createColumn = createColumnHelper<Broadcast>();

export const BroadcastTitleColumn = createColumn.accessor("broadcast_name", {
  header: () => <div className="t-py-3">TITLE</div>,
  size: 25,
  cell: (info) => (
    <div className="t-text-body-sm t-text-text-100 t-line-clamp-2">
      {info.getValue()}
    </div>
  ),
});

export const createBroadcastTypesColumn = (values: BroadcastFilterOptions) =>
  createColumn.accessor("broadcast_types", {
    header: () => (
      <div className="t-py-3 t-flex-1">
        <MultiselectHeader
          options={convertDisplayToOptions(BROADCAST_MEDIUM_OPTIONS)}
          selected={values.BROADCAST_TYPES || null}
          title="MEDIUM"
          filterKey="BROADCAST_TYPES"
        />
      </div>
    ),
    size: 20,
    cell: BroadcastMedium,
    meta: {
      showDivider: true,
    },
  });

export const createBroadcastStatusColumn = (values: BroadcastFilterOptions) =>
  createColumn.accessor("status", {
    header: () => (
      <div className="t-py-3 t-flex-1">
        <MultiselectHeader
          options={convertDisplayToOptions(BROADCAST_STATUSES_DISPLAY)}
          selected={values.STATUS_FILTER || null}
          title="Status"
          filterKey="STATUS_FILTER"
        />
      </div>
    ),
    size: 15,
    cell: (info) => (
      <div className="t-text-body-sm t-text-text-30 t-line-clamp-1">
        <Tag
          tagType={
            BROADCAST_STATUS_COLORS[
              info.getValue() as keyof typeof BROADCAST_STATUS_COLORS
            ] || "red"
          }
          icon={false}
          rounded
        >
          {
            BROADCAST_STATUSES_DISPLAY[
              info.getValue() as keyof typeof BROADCAST_STATUSES_DISPLAY
            ]
          }
        </Tag>
      </div>
    ),
    meta: {
      showDivider: true,
    },
  });

export const createIVRStatusColumn = (values: BroadcastFilterOptions) =>
  createColumn.accessor("ivr_stats.campaign_status", {
    header: () => (
      <div className="t-py-3 t-flex-1">
        <MultiselectHeader
          options={convertDisplayToOptions(IVR_STATUSES_DISPLAY)}
          selected={values.IVR_STATUS || null}
          title="IVR Status"
          filterKey="IVR_STATUS"
        />
      </div>
    ),
    size: 15,
    cell: (info) =>
      info.getValue() ? (
        <div className="t-text-body-sm t-text-text-30 t-line-clamp-1">
          <Tag
            tagType={
              IVR_STATUS_COLORS[
                info.getValue() as keyof typeof IVR_STATUS_COLORS
              ] || "red"
            }
            icon={false}
            rounded
          >
            {
              IVR_STATUSES_DISPLAY[
                info.getValue() as keyof typeof IVR_STATUSES_DISPLAY
              ]
            }
          </Tag>
        </div>
      ) : (
        "-"
      ),
    meta: {
      showDivider: true,
    },
  });

export const BroadcastScheduleColumn = createColumn.accessor("created_at", {
  header: () => (
    <div className="t-py-3 t-flex-1">
      <DateSelectHeader
        startDateKey={FILTER_START_DATE}
        endDateKey={FILTER_END_DATE}
        selectPeriodKey={FILTER_SELECT_PERIOD}
        title="IVR CALL SCHEDULE"
      />
    </div>
  ),
  meta: {
    showDivider: true,
  },
  size: 20,
  cell: (info) => {
    const { scheduled_date, scheduled_time_start, scheduled_time_end } =
      info.row.original.ivr_stats;
    return (
      <div className="t-space-y-1">
        <div className="t-text-body-sm t-text-text-60">
          {scheduled_date ? formatDate(scheduled_date) : "-"}
        </div>
        <div className="t-text-body-sm t-text-text-30">
          {scheduled_time_start &&
            dayjs(scheduled_time_start, "HH:mm").format("hh:mm A")}{" "}
          -{" "}
          {scheduled_time_end &&
            dayjs(scheduled_time_end, "HH:mm").format("hh:mm A")}
        </div>
      </div>
    );
  },
});

export const BroadcastActionsColumn = createColumn.accessor("broadcast_id", {
  header: () => <div className="t-py-3">Actions</div>,
  size: 5,
  cell: (info) => {
    return (
      <BroadcastCallOptions
        broadcastCallId={info.row.original.ivr_stats?.campaign_uuid || ""}
        campaignStatus={info.row.original.ivr_stats?.campaign_status}
      />
    );
  },
});
